import { HeartHandshake, Compass, Lightbulb } from "lucide-react";
import Seo from "@/components/Seo";
import CtaBand from "@/components/CtaBand";
import { FadeIn, Eyebrow, MaskedLine } from "@/components/Reveal";
import { Sparkle } from "@/components/Logo";

const VALUES = [
    "Ética acima de tudo: segurança jurídica não se negocia",
    "Transparência radical: decisões com dados, comunicação clara",
    "Simplicidade inteligente: zero contabilês, tudo explicado",
    "Inovação constante: tecnologia a serviço da beleza",
    "Compromisso com a entrega: prazo cumprido é prioridade",
];

const ARCHETYPES = [
    {
        icon: HeartHandshake,
        title: "Aliado",
        text: "Estamos perto, entendemos a sua rotina e resolvemos junto. Contabilidade com relacionamento de verdade.",
    },
    {
        icon: Compass,
        title: "Mentor",
        text: "Ensinamos e orientamos com responsabilidade, para você entender o próprio negócio e decidir com mais segurança jurídica.",
    },
    {
        icon: Lightbulb,
        title: "Inovador",
        text: "Quebramos os moldes antigos da contabilidade e trazemos tecnologia para modernizar o setor da beleza.",
    },
];

const BASE_URL = "https://www.contabeleza.com.br";

export default function AboutPage() {
    const jsonLd = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "BreadcrumbList",
                itemListElement: [
                    { "@type": "ListItem", position: 1, name: "Início", item: `${BASE_URL}/` },
                    { "@type": "ListItem", position: 2, name: "Sobre nós", item: `${BASE_URL}/sobre` },
                ],
            },
            {
                "@type": "AboutPage",
                name: "Sobre a Contabeleza",
                url: `${BASE_URL}/sobre`,
                inLanguage: "pt-BR",
                description:
                    "Contabilidade especializada no mercado da beleza, com profundo conhecimento da Lei do Salão Parceiro (Lei nº 13.352/2016) e ecossistema integrado ao ilove.me.",
                mainEntity: { "@id": "https://www.contabeleza.com.br/#organizacao" },
            },
        ],
    };

    return (
        <div className="page-bg-sobre">
            <Seo
                title="Sobre Nós, Contabeleza | Especialistas na Lei do Salão Parceiro"
                description="Conheça a Contabeleza: contabilidade especializada no mercado da beleza, com profundo conhecimento da Lei do Salão Parceiro (Lei nº 13.352/2016) e ecossistema integrado ao ilove.me."
                jsonLd={jsonLd}
            />

            <section className="page-hero-sobre relative overflow-hidden px-5 pb-20 pt-32 sm:px-8 sm:pt-40 sm:pb-28">
                <div className="relative mx-auto max-w-7xl text-center">
                    <FadeIn y={16}>
                        <Eyebrow color="text-[#5E11BD]">Sobre nós</Eyebrow>
                    </FadeIn>
                    <h1 className="mx-auto mt-5 max-w-3xl font-display text-4xl leading-[1.08] tracking-tight text-[#08104F] sm:text-5xl lg:text-6xl">
                        <MaskedLine delay={0.1}>Não somos só contadores.</MaskedLine>
                        <MaskedLine delay={0.25}>
                            Somos <span className="text-[#5E11BD]">da beleza.</span>
                        </MaskedLine>
                    </h1>
                    <FadeIn delay={0.45} className="mx-auto mt-6 max-w-xl">
                        <p className="text-base leading-relaxed text-[#08104F]/80 sm:text-lg">
                            Nascemos dentro do mercado da beleza, conhecemos a rotina de salão como ninguém e
                            somos especialistas na lei que organiza esse ecossistema.
                        </p>
                    </FadeIn>
                </div>
            </section>


            <section className="px-5 py-20 sm:px-8 sm:py-28">
                <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-[1.1fr_0.9fr]">
                    <FadeIn>
                        <Eyebrow>Nossa história</Eyebrow>
                        <h2 className="mt-4 font-display text-3xl leading-[1.12] tracking-tight text-[#08104F] sm:text-4xl">
                            A lei que virou propósito
                        </h2>
                        <div className="mt-6 space-y-5 text-base leading-relaxed text-[#3A4768]">
                            <p>
                                A Contabeleza nasceu de uma inquietação: salões, barbearias e clínicas viviam
                                entre a informalidade e o medo, medo de ação trabalhista, de imposto errado, de
                                fiscalização. E a contabilidade tradicional não falava a língua deles.
                            </p>
                            <p>
                                Somos especialistas na{" "}
                                <strong className="font-semibold text-[#08104F]">
                                    Lei nº 13.352/2016, a Lei do Salão Parceiro
                                </strong>, que criou um modelo jurídico para a relação entre estabelecimentos e
                                profissionais da beleza. Estudamos cada artigo a fundo para orientar nossos
                                clientes na adequação, sabendo que o cumprimento da lei depende também das
                                práticas de cada operação.
                            </p>
                            <p>
                                Atendemos negócios da beleza do salão de bairro às grandes redes, com um
                                ecossistema que une contabilidade, gestão e a tecnologia do ilove.me.
                            </p>
                        </div>
                    </FadeIn>

                    <FadeIn delay={0.15}>
                        <div className="relative rounded-3xl border border-[#08104F]/8 bg-[#FFFDFA] p-8 sm:p-10">
                            <Sparkle className="h-7 w-7" />
                            <p className="mt-6 font-display text-2xl leading-snug text-[#08104F]">
                                “Contabeleza: uma empresa que integra gestão, conformidade legal e contabilidade
                                especializada para o mercado da beleza.”
                            </p>
                            <div className="mt-8 border-t border-[#08104F]/10 pt-6">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0047BB]">
                                    Nossos valores
                                </p>
                                <ul className="mt-4 space-y-3">
                                    {VALUES.map((value) => (
                                        <li key={value} className="flex gap-2.5 text-sm leading-relaxed text-[#3A4768]">
                                            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-to-br from-[#FCB21E] to-[#F35E6A]" />
                                            {value}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </FadeIn>
                </div>
            </section>

            <section className="grain relative overflow-hidden bg-[#12223C] px-5 py-20 sm:px-8 sm:py-28">
                <div className="relative mx-auto max-w-7xl">
                    <FadeIn>
                        <Eyebrow light>Como somos</Eyebrow>
                        <h2 className="mt-4 max-w-xl font-display text-3xl leading-[1.12] tracking-tight text-[#F3EDE6] sm:text-4xl">
                            Três jeitos de estar ao seu lado
                        </h2>
                    </FadeIn>
                    <div className="mt-12 grid gap-6 md:grid-cols-3">
                        {ARCHETYPES.map((a, i) => (
                            <FadeIn key={a.title} delay={i * 0.1}>
                                <article className="h-full rounded-3xl border border-white/10 bg-white/[0.04] p-8 transition-colors duration-300 hover:border-[#B2D0FF]/40">
                                    <a.icon className="h-6 w-6 text-[#B2D0FF]" strokeWidth={1.75} />
                                    <h3 className="mt-5 font-display text-xl text-[#F3EDE6]">{a.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-[#B2D0FF]/85">{a.text}</p>
                                </article>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            <CtaBand
                title="Converse com quem entende do seu mundo."
                text="Diagnóstico gratuito com um especialista Contabeleza. Sem compromisso, sem contabilês."
            />
        </div>
    );
}
